import React, { useState } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';
import moment from 'moment';

import tw from '../../../../../styles/tailwind';
import Text from '../../../../../components/Text';
import Icon from '../../../../../components/IcomoonIcon';
import { money } from '../Reports/ReportsSection';
import { MonthSelectorModal } from '../../../../../components/Forms/MonthSelector';


const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]


// Color y lectura según el % de cobertura.
function coverage(pct) {
  if (pct < 40) return { text: 'text-red-500', bar: 'bg-red-500', track: 'bg-red-500/15', label: 'Cobertura baja' }
  if (pct < 70) return { text: 'text-amber-600', bar: 'bg-amber-500', track: 'bg-amber-500/15', label: 'Cobertura media' }
  return { text: 'text-primary-600', bar: 'bg-primary', track: 'bg-primary/15', label: 'Cobertura alta' }
}


function KpiRow({ title, formula, pct }) {
  const c = coverage(pct)
  return (
    <View style={tw`mb-4`}>
      <View style={tw`row justify-between items-center mb-1`}>
        <Text style={tw`text-gray-700 font-500 flex-1 pr-2`}>{title}</Text>
        <Text style={tw.style(`font-700 text-lg`, c.text)}>{pct.toFixed(1)}%</Text>
      </View>

      <View style={tw.style(`h-2 rounded-full mb-1`, c.track)}>
        <View style={tw.style(`h-full rounded-full`, c.bar, { width: `${Math.min(pct, 100)}%` })}></View>
      </View>

      <View style={tw`row justify-between`}>
        <Text style={tw`text-gray-400 text-xs`}>{formula}</Text>
        <Text style={tw.style(`text-xs font-500`, c.text)}>{c.label}</Text>
      </View>
    </View>
  )
}


function StatRow({ label, sub, value, hint }) {
  return (
    <View style={tw`row justify-between items-center py-2`}>
      <View style={tw`flex-1`}>
        <Text style={tw`text-gray-700 font-500`}>{label}</Text>
        {sub ? <Text style={tw`text-gray-400 text-xs`}>{sub}</Text> : null}
      </View>
      <View style={tw`row items-center`}>
        {hint ? <Text style={tw`text-gray-400 text-xs mr-2`}>{hint}</Text> : null}
        <Text style={tw`text-gray-800 font-600`}>{value}</Text>
      </View>
    </View>
  )
}


export default function MoraReportSection({ communityId }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showMonthSelectorModal, setShowMonthSelectorModal] = useState(false);

  const periodLabel = `${MONTHS[selectedMonth.getMonth()]} ${selectedMonth.getFullYear()}`

  // DATOS DE MUESTRA — pendiente endpoint de backend (ver NOTAS-DEV, punto L).
  const r = {
    ideal: 258600,
    M: 67800,   // recaudación puntual del mes
    R: 19800,   // mora recuperada (meses pasados)
    A: 80580,   // adelantados (meses futuros)
    units: { total: 431, al_corriente: 113, en_mora: 318, con_adelanto: 134 },
  }

  const totalAmpliado = r.M + r.R + r.A
  const pReal = r.ideal ? (r.M / r.ideal) * 100 : 0
  const pConMora = r.ideal ? ((r.M + r.R) / r.ideal) * 100 : 0
  const pAmpliada = r.ideal ? ((r.M + r.R + r.A) / r.ideal) * 100 : 0

  const pct = (n) => r.units.total ? `${Math.round((n / r.units.total) * 100)}%` : '—'

  return (
    <ScrollView style={tw`flex-1 bg-[#F3F2F3]`} contentContainerStyle={tw`px-4 pt-4 pb-10`}>

      {/* Cobertura del periodo */}
      <View style={tw`bg-white border border-red rounded-2xl px-5 py-5 mb-4`}>
        <TouchableOpacity
          style={tw`row justify-between items-center bg-gray-100 rounded-xl px-4 py-3 mb-4`}
          onPress={() => setShowMonthSelectorModal(true)}
        >
          <Text style={tw`text-gray-700 font-500`}>{periodLabel}</Text>
          <Icon name='chevron-right' size={13} color={tw.color('gray-700')} style={{ transform: [{ rotateZ: '90deg' }] }} />
        </TouchableOpacity>

        <Text style={tw`text-red/70`}>Cobertura del mes (a tiempo)</Text>
        <Text style={tw`text-red font-700 text-4xl`}>{pReal.toFixed(1)}%</Text>

        <View style={tw`h-2 rounded-full bg-red/15 mt-3 overflow-hidden`}>
          <View style={tw.style(`h-full rounded-full bg-red`, { width: `${Math.min(pReal, 100)}%` })}></View>
        </View>

        <View style={tw`border-t border-red/15 mt-4 pt-3 gap-y-2`}>
          <View style={tw`row justify-between`}>
            <Text style={tw`text-gray-600`}>Ideal del mes</Text>
            <Text style={tw`text-gray-800 font-600`}>{money(r.ideal)}</Text>
          </View>
          <View style={tw`row justify-between`}>
            <Text style={tw`text-gray-600`}>Recaudado a tiempo (M)</Text>
            <Text style={tw`text-gray-800 font-600`}>{money(r.M)}</Text>
          </View>
        </View>
      </View>

      {/* Recaudación del periodo (M / R / A) */}
      <View style={tw`bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-4`}>
        <Text style={tw`text-secondary font-600 text-xl mb-1`}>Recaudación del periodo</Text>

        <StatRow label="Del mes (M)" sub="A tiempo" value={money(r.M)} />
        <StatRow label="Mora recuperada (R)" sub="Meses pasados" value={money(r.R)} />
        <StatRow label="Adelantados (A)" sub="Meses futuros" value={money(r.A)} />

        <View style={tw`row justify-between border-t border-gray-200 mt-1 pt-3`}>
          <Text style={tw`text-gray-800 font-600`}>Total ampliado (M+R+A)</Text>
          <Text style={tw`text-gray-800 font-700`}>{money(totalAmpliado)}</Text>
        </View>
      </View>

      {/* KPIs vs ideal */}
      <View style={tw`bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-4`}>
        <Text style={tw`text-secondary font-600 text-xl mb-4`}>Cobertura vs ideal</Text>

        <KpiRow title="Participación real" formula="M / Ideal" pct={pReal} />
        <KpiRow title="Participación con mora" formula="(M + R) / Ideal" pct={pConMora} />
        <KpiRow title="Participación ampliada" formula="(M + R + A) / Ideal" pct={pAmpliada} />
      </View>

      {/* Por viviendas */}
      <View style={tw`bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-4`}>
        <Text style={tw`text-secondary font-600 text-xl mb-1`}>Viviendas ({r.units.total})</Text>

        <StatRow label="Al corriente" value={r.units.al_corriente} hint={pct(r.units.al_corriente)} />
        <StatRow label="En mora" value={r.units.en_mora} hint={pct(r.units.en_mora)} />
        <StatRow label="Con adelanto" value={r.units.con_adelanto} hint={pct(r.units.con_adelanto)} />
      </View>

      <Text style={tw`text-gray-400 text-xs text-center`}>* Datos de muestra; pendiente de conectar al backend.</Text>

      {
        showMonthSelectorModal &&
          <MonthSelectorModal
            onRequestClose={() => setShowMonthSelectorModal(false)}
            selectedDate={selectedMonth}
            maxDate={moment()}
            onChange={date => {
              setShowMonthSelectorModal(false)
              setSelectedMonth(date)
            }}
          />
      }
    </ScrollView>
  )
}
